// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";

import sharedActionCreators from "@modules/shared/actionCreator";
import * as sharedSelectors from "@modules/shared/selectors";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import type { ApprovableContainer } from "@modules/service/types";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";
import * as accountSelectors from "@modules/account/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreators from "@modules/service/actionCreator";

import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "@components";
import { HierarchyScopeForm } from "@modules/service/components/hierarchy-scope";
import type { HierarchyAssignment } from "@modules/service/types";

type PropsFromState = $Exact<{
  isFirstCareUser: boolean,
  current: ?ApprovableContainer<AbsenceMonitoring>,
  hierarchy: ?HierarchyTree,
  draftAssignments: Array<HierarchyAssignment>,
  isDraftAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setAssignments: typeof actionCreators.SetAbsenceMonitoringAssignments,
  updateAssignments: typeof actionCreators.UpdateAbsenceMonitoringAssignments,
}>;

type OwnProps = $Exact<{
  clientId: number,
  onCancel: () => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

const AbsenceMonitoringEditAssignmentsModal = props => {
  const handleCancel = () => {
    props.onCancel();
    props.setAssignments([]);
  };

  const handleSubmitAbsenceMonitoringDraft = () => {
    if (props.current && props.current.hierarchyAssignments) {
      props.updateAssignments({
        container: props.current,
        assignments: props.draftAssignments,
      });
      handleCancel();
    }
  };

  const handleChanges = (data: Array<HierarchyAssignment>) => {
    props.setAssignments(data);
  };

  return (
    <Modal className="modal-large">
      <ModalHeader className="d-flex justify-content-between" toggle={handleCancel}>
        {props.t("ABSENCE_MONITORING_EDIT_HIERARCHY_SCOPE_TITLE", {
          ...(props.current ? { processId: props.current.id } : {}),
        })}
      </ModalHeader>
      <ModalBody className="d-flex flex-column align-content-center">
        <div className="w-100 d-flex justify-content-center">
          <div className="col-sm-12">
            {props.hierarchy && props.draftAssignments ? (
              <HierarchyScopeForm
                hierarchy={props.hierarchy}
                assignments={props.draftAssignments}
                onChange={handleChanges}
              />
            ) : null}
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={handleCancel}>
          {props.t("CANCEL")}
        </Button>
        <Button
          materialIcon="save"
          onClick={handleSubmitAbsenceMonitoringDraft}
          disabled={!props.isDraftAssignmentsValid}
        >
          {props.t("SAVE")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    isFirstCareUser: accountSelectors.isFirstCareUser(state),
    current: serviceSelectors.getCurrentAbsenceMonitoring(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
    draftAssignments: serviceSelectors.getAbsenceMonitoringDraftAssignments(state),
    isDraftAssignmentsValid: serviceSelectors.isAbsenceMonitoringDraftAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setAssignments: actionCreators.SetAbsenceMonitoringDraftAssignments,
  updateAssignments: actionCreators.UpdateAbsenceMonitoringAssignments,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceMonitoringEditAssignmentsModal);
